<template>
  <a-spin :spinning="props.loading">
    <Draggable
      :value="[...props.images]"
      class="images-loader"
      :animation="300"
      draggable=".images-loader__item"
      filter=".images-loader__load-section"
      ghostClass="images-loader__item--ghost"
      @end="onSortImages"
    >
      <div
        v-for="(file, index) in props.images"
        :key="index"
        class="images-loader__item images-loader__item--sortable"
      >
        <div class="images-loader__thumbnail">
          <img
            :src="getImageSrc(file)"
            alt=""
          />
        </div>

        <div class="images-loader__control">
          <a-icon
            type="edit"
            style="cursor: pointer"
            @mousedown.prevent
            @click="onOpenCropModal(index)"
            @touchdown="onOpenCropModal(index)"
          />
          <a-icon
            type="delete"
            style="cursor: pointer"
            @mousedown.prevent
            @click="removeFileByIndex(index)"
            @touchdown="removeFileByIndex(index)"
          />
        </div>
      </div>

      <template v-if="!showUpload">
        <div class="images-loader__load-section">
          <div
            class="images-loader__load-section__item"
            @click="onChooseFiles"
          >
            <span class="images-loader__icon">
              <a-icon type="upload" />
            </span>
          </div>

          <div class="images-loader__requirements">
            <div v-if="showMinResolutionRequirement">
              {{
                $t("offers.image_requirements.resolution", {
                  height: ratio.minHeight,
                  width: ratio.minWidth
                })
              }}
            </div>

            <div v-if="maxKb">
              {{
                $t("offers.image_requirements.max_size", {
                  size: maxKb / 1024
                })
              }}
            </div>

            <div>
              {{
                $t("offers.image_requirements.formats", {
                  list: allowedFormats
                })
              }}
            </div>
          </div>
        </div>
      </template>
    </Draggable>

    <div
      v-if="errorMessage"
      class="images-loader__error"
    >
      {{ errorMessage }}
    </div>

    <input
      type="file"
      multiple
      ref="fileInputRef"
      :accept="allowedFormats"
      :style="{ display: 'none' }"
      @change="addFiles"
    />

    <ImageCropper
      :visible="cropModalVisible"
      :croppedImage="editedImage"
      @close="onCloseCropModal"
      @onCropped="handleCropImage"
    />
  </a-spin>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue"
import { TranslateResult } from "vue-i18n"
import Draggable, { MoveEvent } from "vuedraggable"

import i18n from "@/i18n"
import { getFullImagePath } from "@/utils/cdn"

import ImageCropper from "./ImageCropper.vue"

type ImageRatio = {
  [key: string]: number
}
type ImageCropData = {
  index: number
  file: File
}
type ImageLoaderProps = {
  ratio?: ImageRatio
  images?: Array<{ filename: string; on_cdn: boolean } | string | File>
  limit?: number
  loading?: boolean
  maxKb: number
}
type Emits = {
  (event: "removeImage", index: number): void
  (event: "addImage", file: File): void
  (event: "sortImages", sortEvent: any): void
  (event: "changeImage", changeData: ImageCropData)
}
type SortableMoveEvent = {
  oldIndex: number
  newIndex: number
}

const props = defineProps<ImageLoaderProps>()
const emit = defineEmits<Emits>()

const fileInputRef = ref<HTMLInputElement>()

const showMinResolutionRequirement = computed(() =>
  Boolean(props.ratio?.minHeight && props.ratio?.minWidth)
)

// CHOOSE
const showUpload = computed<boolean>(() => props.images.length >= props.limit)

const onChooseFiles = () => {
  fileInputRef.value.click()
}

// VALIDATIONS
const allowedFormats = ".jpg, .jpeg, .png"
const fileTypes = ["image/jpeg", "image/pjpeg", "image/png"]

const isValidFormat = (file: File): boolean => {
  return fileTypes.includes(file.type)
}
const isValidSize = (file: File): boolean => {
  return Boolean(file.size / 1024 < +props.maxKb)
}
const isValidRatio = (file: File) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    const { maxWidth = Infinity, maxHeight = Infinity, minWidth = 0, minHeight = 0 } = props.ratio

    reader.onload = () => {
      const img = new Image()

      img.onload = () => {
        const { width, height } = img

        if (width < minWidth || width > maxWidth || height < minHeight || height > maxHeight) {
          reject()
        } else {
          resolve(file)
        }
      }

      img.src = reader.result as string
    }

    reader.readAsDataURL(file)
  })
}
const getFormat = (filename: string): string => {
  return filename.split(".").pop()
}

// ERRORS
const errorMessage = ref<TranslateResult>("")

const errors = {
  limit: i18n.tc("common.max_file_load", +props.limit),
  formats: `${i18n.t("common.invalid_data")} ${allowedFormats}`,
  ratio: `${i18n.t("common.invalid_ratio")}`,
  size: i18n.tc("common.max_file_size", +(props.maxKb / 1024).toFixed(2))
}

const showError = (type) => {
  errorMessage.value = errors[type]
}

// PREVIEW
const getImageSrc = (path: string | File | { filename: string; on_cdn: boolean }) => {
  if (typeof path === "string") {
    return path.startsWith("http") ? path : getFullImagePath(path, "full")
  }

  if (path instanceof File) {
    return URL.createObjectURL(path)
  }

  return getFullImagePath(path.filename, "full")
}

// ADD
const createReadPromise = (file: File): Promise<File | undefined> => {
  return new Promise(async (resolve) => {
    if (!isValidSize(file)) {
      showError("size")
      return resolve(undefined)
    }

    if (!isValidFormat(file)) {
      showError("format")
      return resolve(undefined)
    }

    if (props.ratio) {
      try {
        await isValidRatio(file)
      } catch (err) {
        showError("ratio")
        return resolve(undefined)
      }
    }

    resolve(file)
  })
}
const addFiles = async (event: InputEvent): Promise<void> => {
  const { files: selectedFiles } = event.target as HTMLInputElement

  if (props.images.length + selectedFiles.length > props.limit) {
    showError("limit")
    return
  }

  errorMessage.value = ""

  for (let i = 0; i < selectedFiles.length; i++) {
    const image = await createReadPromise(selectedFiles[i])

    if (!image) continue

    emit("addImage", image)
  }

  fileInputRef.value.value = null
}

// SORT
const onSortImages = (sortEvent: SortableMoveEvent & MoveEvent<HTMLDivElement>) => {
  if (sortEvent.oldIndex !== sortEvent.newIndex) {
    emit("sortImages", sortEvent)
  }
}

// REMOVE
const removeFileByIndex = (index: number) => {
  if (index < 0) return

  emit("removeImage", index)
}

// CROP
const editedImage = ref<any>()
const cropModalVisible = ref<boolean>(false)

const onOpenCropModal = (index: number) => {
  const image = props.images[index]

  if (image instanceof File) {
    editedImage.value = {
      preview: getImageSrc(image),
      filename: image.name,
      format: image.type.split("/").at(-1)
    }
  } else if (typeof image === "string") {
    editedImage.value = { preview: getImageSrc(image), filename: image, format: getFormat(image) }
  } else {
    editedImage.value = { ...image, preview: getImageSrc(image), format: getFormat(image.filename) }
  }

  cropModalVisible.value = true
}
const handleCropImage = async (file: File) => {
  const index = props.images.findIndex((item) => {
    if (typeof item === "string") {
      return item === editedImage.value.filename
    }

    if (item instanceof File) {
      return item.name === editedImage.value.filename
    }

    return item.filename === editedImage.value.filename
  })

  emit("changeImage", { index, file })
  onCloseCropModal()
}

// CLOSE
const onCloseCropModal = () => {
  cropModalVisible.value = false
  editedImage.value = undefined
}
</script>

<style lang="scss" scoped>
.images-loader {
  $root: &;
  line-height: initial;
  display: inline-flex;
  flex-flow: row wrap;
  gap: 4px 8px;
  position: relative;
  margin: 0;
  padding: 0;

  &__item {
    word-wrap: break-word;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    &--sortable {
      cursor: move;
    }

    &--dragging {
      opacity: 1;
      cursor: move;
    }

    &--ghost {
      opacity: 0;
    }
  }

  &__error {
    color: #f5222d;
    line-height: 1.524;
    display: flex;
  }

  &__thumbnail {
    width: 90px;
    height: 90px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #fff;
    font-size: 0;
    line-height: 0;
    border: 1px solid #e9e9e9;
    border-radius: 2px;
    overflow: hidden;

    & img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  &__control {
    width: 100%;
    height: 100%;
    border-radius: 2px;

    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;

    position: absolute;
    top: 0;
    right: 0;

    color: #fff;
    background-color: rgba(0, 0, 0, 0.65);
    font-size: 16px;

    opacity: 0;
    transition: opacity 0.3s;

    &:hover {
      opacity: 1;
    }
  }

  &__load-section {
    display: flex;
    gap: 8px;

    &__item {
      width: 90px;
      height: 90px;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      background-color: #fafafa;
      border: 1px dashed #d9d9d9;
      border-radius: 2px;
      overflow: hidden;
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);

      &--disabled {
        opacity: 0.5;
        background: #fafafa;
        cursor: not-allowed;
      }

      &:hover {
        border-color: $brand-color-6;
        color: $brand-color-6;
      }
    }
  }

  &__requirements {
    color: #00000040;
    font-size: 12px;
    line-height: 20px;
    width: fit-content;
  }
}
</style>
